(function (root, factory) {
  const lifecycle = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = lifecycle;
  }

  if (root) {
    root.MarineMaxAIChatWidgetLifecycle = lifecycle;
  }
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  const desktopMediaQuery = '(min-width: 1025px)';
  const externalOwnerKey = 'MarineMaxAIChatExternalOwner';
  const mobileCtaHeightProperty = '--ai-chat-mobile-cta-height';
  const mobileCtaOwnersKey = 'MarineMaxAIChatMobileCtaOwners';
  const pricingHash = '#request-pricing';
  const scrollLockClass = 'chat-scroll-locked';
  const scrollLockOwnersKey = 'MarineMaxAIChatScrollLockOwners';

  function resolveDesktopInitialView(chatRoot, documentRef) {
    const widget = chatRoot ? chatRoot.closest('.ai-chat-widget') : null;
    const isInline = Boolean(
      widget && widget.classList.contains('ai-chat-widget--inline'),
    );
    const rightRailExperiment =
      !isInline && documentRef
        ? documentRef.querySelector(
            '[data-ai-chat-experiment-config][data-ai-chat-layout="right_rail"]',
          )
        : null;

    return String(
      (rightRailExperiment && rightRailExperiment.dataset.desktopInitialView) ||
        (chatRoot && chatRoot.dataset.desktopInitialView) ||
        'chat',
    );
  }

  function initializeChatInstance(options = {}) {
    const documentRef = options.document || (root && root.document) || null;
    const windowRef = options.window || root;
    const instanceId = String(options.instanceId || '');
    const initialize = options.initialize;
    const onError = typeof options.onError === 'function' ? options.onError : () => {};
    const setTimer =
      options.setTimeout ||
      (windowRef && typeof windowRef.setTimeout === 'function'
        ? windowRef.setTimeout.bind(windowRef)
        : null);

    if (!documentRef || !instanceId || typeof initialize !== 'function') {
      throw new Error('AI Chat instance bootstrap dependencies are incomplete');
    }

    function reportError(error, context) {
      try {
        onError(error, context);
      } catch (_) {}
    }

    function run(chatRoot, checkExperiment) {
      if (!chatRoot || chatRoot.dataset.aiChatInitialized === 'true') return false;

      if (checkExperiment) {
        const widget = chatRoot.closest('.ai-chat-widget');
        const isInline = Boolean(
          widget && widget.classList.contains('ai-chat-widget--inline'),
        );
        const experiment = documentRef.querySelector(
          '[data-ai-chat-experiment-config][data-ai-chat-layout="right_rail"]',
        );
        if (
          !isInline &&
          experiment &&
          experiment.dataset.aiChatEnabled === 'false'
        ) {
          const staticSection = widget ? widget.closest('.shopify-section') : null;
          if (staticSection) staticSection.hidden = true;
          return false;
        }
      }

      chatRoot.dataset.aiChatInitialized = 'true';
      try {
        initialize(chatRoot);
        return true;
      } catch (error) {
        delete chatRoot.dataset.aiChatInitialized;
        throw error;
      }
    }

    try {
      const chatRoot = documentRef.getElementById(instanceId);
      if (chatRoot) return run(chatRoot, true);

      if (setTimer) {
        setTimer(() => {
          try {
            run(documentRef.getElementById(instanceId), false);
          } catch (error) {
            reportError(error, { instanceId, phase: 'delayed-initialize' });
          }
        }, options.retryDelay ?? 1000);
      }
      return false;
    } catch (error) {
      reportError(error, { instanceId, phase: 'initialize' });
      return false;
    }
  }

  function createWidgetLifecycle(options = {}) {
    const chatRoot = options.root;
    const windowRef = options.window || root;
    const documentRef =
      options.document ||
      (chatRoot && chatRoot.ownerDocument) ||
      (windowRef && windowRef.document) ||
      null;
    const onChat = options.onChat || (() => {});
    const onContactSales = options.onContactSales || (() => {});
    const onPricing = options.onPricing || (() => {});
    const onPromptInteraction = options.onPromptInteraction || (() => {});
    const ResizeObserverClass =
      options.ResizeObserver ||
      (windowRef && typeof windowRef.ResizeObserver === 'function'
        ? windowRef.ResizeObserver
        : null);
    const matchMedia =
      options.matchMedia ||
      (windowRef && typeof windowRef.matchMedia === 'function'
        ? windowRef.matchMedia.bind(windowRef)
        : null);
    const requestFrame =
      options.requestAnimationFrame ||
      (typeof windowRef.requestAnimationFrame === 'function'
        ? windowRef.requestAnimationFrame.bind(windowRef)
        : (callback) => callback());

    if (!chatRoot || !windowRef || !documentRef) {
      throw new Error('AI Chat widget lifecycle dependencies are incomplete');
    }

    const widget = chatRoot.closest('.ai-chat-widget');
    const isInline = Boolean(
      widget && widget.classList.contains('ai-chat-widget--inline'),
    );
    const desktopInitialView = resolveDesktopInitialView(chatRoot, documentRef);
    const listenerDisposers = [];
    let initialized = false;
    let lastOpenTrigger = null;
    let mobileCtaResizeObserver = null;

    function addListener(element, eventName, listener, listenerOptions) {
      if (!element || typeof element.addEventListener !== 'function') return;
      element.addEventListener(eventName, listener, listenerOptions);
      listenerDisposers.push(() => {
        if (typeof element.removeEventListener === 'function') {
          element.removeEventListener(eventName, listener, listenerOptions);
        }
      });
    }

    function getOpenButtons() {
      return widget ? Array.from(widget.querySelectorAll('.open-chat')) : [];
    }

    function getCloseButtons() {
      return widget ? Array.from(widget.querySelectorAll('.close-chat')) : [];
    }

    function getMobileCta() {
      return widget ? widget.querySelector('[data-ai-chat-mobile-cta]') : null;
    }

    function getMobileCtaButtons() {
      return widget
        ? Array.from(widget.querySelectorAll('.ai-chat-mobile-cta__button'))
        : [];
    }

    function isMobile() {
      return Number(windowRef.innerWidth) <= 1024;
    }

    function isDesktopFormOnly() {
      return Boolean(
        desktopInitialView === 'form_only' &&
          matchMedia &&
          matchMedia(desktopMediaQuery).matches,
      );
    }

    function syncAccessibility() {
      const isVisible = !isMobile() || chatRoot.classList.contains('active');
      chatRoot.setAttribute('aria-hidden', String(!isVisible));
      new Set([...getOpenButtons(), ...getMobileCtaButtons()]).forEach((button) => {
        button.setAttribute('aria-expanded', String(isVisible));
      });
    }

    function resolveTrigger(trigger) {
      const candidate =
        trigger && (trigger.currentTarget || trigger.target || trigger);
      return candidate && typeof candidate.focus === 'function' ? candidate : null;
    }

    function focusInitialControl() {
      if (!isMobile() || !chatRoot.classList.contains('active')) return;

      const hubSpotContainer = chatRoot.querySelector('.ai-chat__hubspot');
      const dynamicInput = chatRoot.querySelector(
        '[data-ai-chat-pricing-name], [data-ai-chat-email-address], [data-ai-chat-verification-code]',
      );
      const target =
        (hubSpotContainer && hubSpotContainer.style.display !== 'none'
          ? hubSpotContainer.querySelector('[name="firstname"]')
          : null) ||
        dynamicInput ||
        chatRoot.querySelector('.ai-chat__input textarea') ||
        getCloseButtons()[0];

      if (target && typeof target.focus === 'function') target.focus();
    }

    function scheduleInitialFocus() {
      requestFrame(focusInitialControl);
    }

    function getOwnerSet(key) {
      if (!(windowRef[key] instanceof Set)) windowRef[key] = new Set();
      return windowRef[key];
    }

    function lockScroll() {
      if (!isMobile() || isInline) return;
      getOwnerSet(scrollLockOwnersKey).add(chatRoot);
      documentRef.documentElement.classList.add(scrollLockClass);
      documentRef.body.classList.add(scrollLockClass);
    }

    function unlockScroll() {
      if (isInline) return;
      const owners = getOwnerSet(scrollLockOwnersKey);
      owners.delete(chatRoot);
      if (owners.size > 0) return;
      delete windowRef[scrollLockOwnersKey];
      documentRef.documentElement.classList.remove(scrollLockClass);
      documentRef.body.classList.remove(scrollLockClass);
    }

    function open(trigger) {
      const resolvedTrigger = resolveTrigger(trigger);
      if (resolvedTrigger) lastOpenTrigger = resolvedTrigger;
      chatRoot.classList.add('active');
      if (widget) widget.classList.add('chat-open');
      getOpenButtons().forEach((button) => button.classList.add('hide'));
      lockScroll();
      syncAccessibility();
      scheduleInitialFocus();
      return true;
    }

    function openChat(trigger) {
      const resolvedTrigger = resolveTrigger(trigger);
      if (
        resolvedTrigger &&
        resolvedTrigger.classList.contains('ai-chat-mobile-cta__button--chat')
      ) {
        onPromptInteraction(resolvedTrigger);
      }
      onChat();
      return open(trigger);
    }

    function close(options = {}) {
      chatRoot.classList.remove('active');
      if (widget) widget.classList.remove('chat-open');
      getOpenButtons().forEach((button) => button.classList.remove('hide'));
      unlockScroll();
      syncAccessibility();
      if (isMobile() && options.restoreFocus !== false && lastOpenTrigger) {
        requestFrame(() => lastOpenTrigger.focus());
      }
      return true;
    }

    function claimExternalOwnership() {
      if (!windowRef[externalOwnerKey]) {
        windowRef[externalOwnerKey] = chatRoot.id;
      }
      return ownsExternalTriggers();
    }

    function ownsExternalTriggers() {
      if (!windowRef[externalOwnerKey]) {
        windowRef[externalOwnerKey] = chatRoot.id;
      }
      return windowRef[externalOwnerKey] === chatRoot.id;
    }

    function shouldOfferUnlockPricing() {
      return chatRoot.dataset.showUnlockPricing === 'true';
    }

    function openPricing(trigger) {
      open(trigger);
      onPricing();
    }

    function openContactSales(trigger) {
      open(trigger);
      if (trigger) onPromptInteraction(trigger);
      onContactSales({ returnTo: isMobile() && !isInline ? 'pdp' : 'chat' });
    }

    function ownsContactSalesTrigger(trigger) {
      const triggerWidget =
        trigger && typeof trigger.closest === 'function'
          ? trigger.closest('.ai-chat-widget')
          : null;
      return triggerWidget ? triggerWidget === widget : ownsExternalTriggers();
    }

    function updatePricingHash() {
      if (windowRef.location.hash === pricingHash) return;
      if (
        windowRef.history &&
        typeof windowRef.history.replaceState === 'function'
      ) {
        windowRef.history.replaceState(
          windowRef.history.state,
          documentRef.title,
          `${windowRef.location.pathname}${windowRef.location.search}${pricingHash}`,
        );
        return;
      }
      windowRef.location.hash = pricingHash;
    }

    function handleContactSalesClick(event) {
      const trigger = event.currentTarget || event.target;
      if (!ownsContactSalesTrigger(trigger)) return;
      event.preventDefault();
      openContactSales(trigger);
    }

    function handlePricingClick(event) {
      if (!ownsExternalTriggers() || !shouldOfferUnlockPricing()) return;
      event.preventDefault();
      updatePricingHash();
      openPricing(event.currentTarget || event.target);
    }

    function handleHashChange() {
      if (
        ownsExternalTriggers() &&
        windowRef.location.hash === pricingHash &&
        shouldOfferUnlockPricing()
      ) {
        openPricing();
      }
    }

    function syncViewport() {
      if (!widget) return;
      const viewportHeight = windowRef.visualViewport
        ? windowRef.visualViewport.height
        : windowRef.innerHeight;
      widget.style.setProperty(
        '--ai-chat-runtime-vh',
        `${Math.round(viewportHeight)}px`,
      );
    }

    function syncMobileCtaHeight() {
      if (!initialized || isInline || !isMobile()) return;
      const mobileCta = getMobileCta();
      const documentStyle = documentRef.documentElement
        ? documentRef.documentElement.style
        : null;
      if (!mobileCta || !documentStyle) return;

      const mobileCtaHeight = Math.ceil(Number(mobileCta.offsetHeight) || 0);
      if (mobileCtaHeight > 0) {
        documentStyle.setProperty(mobileCtaHeightProperty, `${mobileCtaHeight}px`);
      }
    }

    function scheduleMobileCtaHeightSync() {
      requestFrame(syncMobileCtaHeight);
    }

    function observeMobileCtaHeight() {
      if (isInline || !ResizeObserverClass || mobileCtaResizeObserver) return;
      const mobileCta = getMobileCta();
      if (!mobileCta) return;
      mobileCtaResizeObserver = new ResizeObserverClass(
        scheduleMobileCtaHeightSync,
      );
      mobileCtaResizeObserver.observe(mobileCta);
    }

    function handleViewportChange() {
      syncViewport();
      syncAccessibility();
      scheduleMobileCtaHeightSync();
      observeMobileCtaHeight();
    }

    function handleKeydown(event) {
      if (
        event.key !== 'Escape' ||
        !isMobile() ||
        !chatRoot.classList.contains('active')
      ) {
        return;
      }
      event.preventDefault();
      close();
    }

    function syncWidgetModeClasses() {
      if (!widget) return;
      widget.classList.toggle(
        'ai-chat-widget--desktop-form-only',
        desktopInitialView === 'form_only',
      );
      const hasStickyMobileCta = Boolean(getMobileCta());
      const hasLegacyFullWidthLauncher = getOpenButtons().some(
        (button) => button.classList.contains('open-chat--full-width'),
      );
      if ((hasStickyMobileCta || hasLegacyFullWidthLauncher) && !isInline) {
        getOwnerSet(mobileCtaOwnersKey).add(chatRoot);
        documentRef.body.classList.add('ai-chat-mobile-cta-enabled');
      }
    }

    function initialize() {
      if (initialized) return false;
      initialized = true;
      claimExternalOwnership();
      syncWidgetModeClasses();
      syncViewport();
      syncAccessibility();
      scheduleMobileCtaHeightSync();
      observeMobileCtaHeight();

      getOpenButtons().forEach((button) => addListener(button, 'click', openChat));
      getCloseButtons().forEach((button) => addListener(button, 'click', close));
      documentRef
        .querySelectorAll('[data-ai-chat-contact-sales]')
        .forEach((link) => addListener(link, 'click', handleContactSalesClick));
      documentRef
        .querySelectorAll(`a[href="${pricingHash}"]`)
        .forEach((link) => addListener(link, 'click', handlePricingClick));
      addListener(windowRef, 'hashchange', handleHashChange);
      addListener(windowRef, 'resize', handleViewportChange, { passive: true });
      addListener(documentRef, 'keydown', handleKeydown);
      if (windowRef.visualViewport) {
        addListener(windowRef.visualViewport, 'resize', handleViewportChange, {
          passive: true,
        });
      }

      handleHashChange();
      return true;
    }

    function destroy() {
      close({ restoreFocus: false });
      listenerDisposers.splice(0).forEach((dispose) => dispose());
      if (mobileCtaResizeObserver) {
        mobileCtaResizeObserver.disconnect();
        mobileCtaResizeObserver = null;
      }
      if (ownsExternalTriggers()) delete windowRef[externalOwnerKey];
      const mobileCtaOwners = getOwnerSet(mobileCtaOwnersKey);
      mobileCtaOwners.delete(chatRoot);
      if (mobileCtaOwners.size === 0) {
        delete windowRef[mobileCtaOwnersKey];
        documentRef.body.classList.remove('ai-chat-mobile-cta-enabled');
        if (
          documentRef.documentElement &&
          documentRef.documentElement.style &&
          typeof documentRef.documentElement.style.removeProperty === 'function'
        ) {
          documentRef.documentElement.style.removeProperty(mobileCtaHeightProperty);
        }
      }
      if (widget && typeof widget.style.removeProperty === 'function') {
        widget.style.removeProperty('--ai-chat-runtime-vh');
      }
      initialized = false;
      lastOpenTrigger = null;
    }

    return Object.freeze({
      close,
      desktopInitialView,
      destroy,
      initialize,
      isDesktopFormOnly,
      open,
      openChat,
      openContactSales,
      openPricing,
      ownsExternalTriggers,
      syncAccessibility,
      syncMobileCtaHeight,
      syncViewport,
    });
  }

  return Object.freeze({
    createWidgetLifecycle,
    initializeChatInstance,
    resolveDesktopInitialView,
  });
});
